import { customerApi } from './api';
import { AddressDto } from '../types';

// Interface for coordinates
export interface Coordinates {
  latitude: number;
  longitude: number;
}

// Interface for address
export interface Address extends AddressDto {}

/**
 * Calculate the distance between two points using the Haversine formula
 * @param point1 First point coordinates
 * @param point2 Second point coordinates
 * @returns Distance in kilometers
 */
export const calculateDistance = (point1: Coordinates, point2: Coordinates): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(point2.latitude - point1.latitude);
  const dLon = toRadians(point2.longitude - point1.longitude);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRadians(point1.latitude)) * Math.cos(toRadians(point2.latitude)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in kilometers
  
  return Math.round(distance * 10) / 10; // Round to 1 decimal place
};

/**
 * Convert degrees to radians
 * @param degrees Angle in degrees
 * @returns Angle in radians
 */
const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

/**
 * Geocode an address to get coordinates using the API
 * @param address Address to geocode
 * @returns Promise that resolves to coordinates
 */
export const geocodeAddress = async (address: Address): Promise<Coordinates> => {
  try {
    const coords = await customerApi.getCoordinates(address);
    return {
      latitude: coords.latitude,
      longitude: coords.longitude
    };
  } catch (error) {
    console.error('Error geocoding address:', error);
    throw error;
  }
};

/**
 * Find the appropriate delivery fee and time based on distance and radius configuration
 * @param distance Distance in kilometers
 * @param radiusConfig Array of radius configurations
 * @returns Object with fee and estimated time
 */
export const getDeliveryInfoByDistance = (
  distance: number, 
  radiusConfig: Array<{radiusKm: number, fee: number, estimatedTime: string}>
): {fee: number, time: string} => {
  if (!radiusConfig || radiusConfig.length === 0) {
    return {
      fee: 0,
      time: '30-45 min'
    };
  }

  // Sort radius config by radius (ascending)
  const sortedConfig = [...radiusConfig].sort((a, b) => a.radiusKm - b.radiusKm);
  
  // Find the appropriate radius tier
  for (const config of sortedConfig) {
    if (distance <= config.radiusKm) {
      return {
        fee: config.fee,
        time: config.estimatedTime
      };
    }
  }
  
  // If distance is greater than all configured radii, use the last one
  const lastConfig = sortedConfig[sortedConfig.length - 1];
  return {
    fee: lastConfig.fee,
    time: lastConfig.estimatedTime
  };
};
